/** Skills #283: keep the catalogue, person assignment and schedule requirements connected. */
import { ApiError } from '@/api/client';
import { type ApiUser, useShiftTypes, useUsers } from '@/api/data';
import { type ApiSkill, type SkillWrite, useSkillWrites, useSkills } from '@/api/skills';
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { Button, Drawer, toast } from '@/ui';
import { Icon } from '@/ui/icons';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import s from './Ferdigheter.module.css';

const message = (e: unknown) => (e instanceof Error ? e.message : T('Kunne ikke lagre. Prøv igjen.'));
const fullName = (u: ApiUser) => `${u.name ?? ''} ${u.lastname ?? ''}`.trim() || T('Ansatt {n}', { n: u.id });
const personUrl = (userId: number, skillId: number) =>
  `/ansatte/${userId}?fra=ferdigheter&ferdighet=${skillId}#ferdigheter`;
type ShiftType = { id: number; name: string | null };

export function Ferdigheter({ mayEdit }: { mayEdit: boolean }) {
  const q = useSkills();
  const typesQ = useShiftTypes();
  const usersQ = useUsers();
  const can = useCan();
  const location = useLocation();
  const w = useSkillWrites();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [problem, setProblem] = useState('');
  const [removing, setRemoving] = useState<ApiSkill | null>(null);
  const [removeProblem, setRemoveProblem] = useState('');
  const [removeConflict, setRemoveConflict] = useState(false);
  const createInput = useRef<HTMLInputElement>(null);
  const newButton = useRef<HTMLButtonElement>(null);
  const createLock = useRef(false);
  const skills = q.data ?? [];
  const types = (typesQ.data ?? []).filter((t) => t.name);
  const canAssign = can('ansatte.endre');
  const canRead = can('vaktplan.se') || can('innstillinger.endre') || canAssign;
  const reload = async () => {
    const result = await q.refetch();
    return result.isError ? null : (result.data ?? []);
  };

  useEffect(() => {
    if (creating) createInput.current?.focus();
  }, [creating]);
  useEffect(() => {
    if (q.isSuccess && /^#ferdighet-\d+$/.test(location.hash)) {
      const el = document.getElementById(location.hash.slice(1));
      el?.scrollIntoView({ block: 'center' });
      el?.focus({ preventScroll: true });
    }
  }, [q.isSuccess, location.hash]);
  const closeCreate = () => {
    setCreating(false);
    setName('');
    setProblem('');
    requestAnimationFrame(() => newButton.current?.focus());
  };
  const create = async () => {
    if (createLock.current) return;
    const clean = name.trim();
    if (!clean || clean.length > 80) {
      setProblem(T('Skriv et navn på 1 til 80 tegn.'));
      createInput.current?.focus();
      return;
    }
    createLock.current = true;
    setProblem('');
    try {
      const made = await w.create.mutateAsync({ name: clean });
      closeCreate();
      toast(T('Ferdigheten er opprettet. Velg ansatte som har den.'));
      requestAnimationFrame(() => document.getElementById(`ferdighet-${made.id}`)?.focus());
    } catch (e) {
      setProblem(message(e));
    } finally {
      createLock.current = false;
    }
  };
  const remove = async () => {
    if (!removing || w.remove.isPending) return;
    setRemoveProblem('');
    try {
      await w.remove.mutateAsync(removing);
      setRemoving(null);
      toast(T('Ferdigheten er slettet.'));
    } catch (e) {
      setRemoveProblem(message(e));
      setRemoveConflict(e instanceof ApiError && e.code === 'skills_changed');
    }
  };

  if (!canRead)
    return (
      <p className={s.notice}>
        {T('Du har ikke tilgang til å se ferdigheter. Spør administratoren om tilgang.')}
      </p>
    );
  if (q.isPending)
    return (
      <output className={s.loading}>
        <span className={s.skeleton} />
        <span className={s.skeleton} />
        {T('Henter ferdigheter …')}
      </output>
    );
  if (q.isError && !q.data)
    return (
      <div className={s.notice} role="alert">
        <p>{T('Kunne ikke hente ferdighetene. Ingenting er endret.')}</p>
        <Button onClick={() => void reload()}>{T('Prøv igjen')}</Button>
      </div>
    );

  return (
    <section className={s.root} aria-label={T('Ferdigheter')}>
      <div className={s.toolbar}>
        <p>{T('Knytt kompetanse til vaktene som trenger den. Tildel ferdigheter på ansattkortet.')}</p>
        {mayEdit && (
          <button
            ref={newButton}
            type="button"
            className="btn pri"
            onClick={() => {
              setCreating(true);
              setProblem('');
            }}
            disabled={creating || q.isError}
          >
            <Icon name="plus" />
            {T('Ny ferdighet')}
          </button>
        )}
      </div>
      {q.isError && (
        <div className={s.notice} role="alert">
          <p>{T('Viser sist hentede ferdigheter. Hent siste versjon før du endrer noe.')}</p>
          <Button onClick={() => void reload()}>{T('Hent siste versjon')}</Button>
        </div>
      )}
      {!mayEdit && (
        <p className={s.hint}>
          {T('Du har lesetilgang. Endringer i navn og krav krever tilgang til driftsinnstillinger.')}
        </p>
      )}
      {creating && (
        <form
          className={s.create}
          onSubmit={(e) => {
            e.preventDefault();
            void create();
          }}
        >
          <label htmlFor="new-skill-name">{T('Hva heter ferdigheten?')}</label>
          <p className={s.hint} id="new-skill-help">
            {T('Bruk et navn ansatte kjenner igjen, for eksempel Nøkler eller Skiftleder.')}
          </p>
          <div className={s.createActions}>
            <input
              ref={createInput}
              id="new-skill-name"
              className="inp"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              aria-invalid={!!problem}
              aria-describedby={problem ? 'new-skill-error' : 'new-skill-help'}
              disabled={w.create.isPending}
            />
            <Button type="submit" tone="pri" disabled={w.create.isPending}>
              {w.create.isPending ? T('Oppretter …') : T('Opprett ferdighet')}
            </Button>
            <Button ghost onClick={closeCreate} disabled={w.create.isPending}>
              {T('Avbryt')}
            </Button>
          </div>
          {problem && (
            <p id="new-skill-error" role="alert" className={s.error}>
              {problem}
            </p>
          )}
        </form>
      )}
      {!skills.length && !creating && (
        <div className={s.empty}>
          <Icon name="users" />
          <h3>{T('Riktig kompetanse på riktig vakt')}</h3>
          <p>
            {T(
              'Legg til ferdigheter dere trenger, som Nøkler eller Skiftleder. Velg deretter ansatte og vakttypene som krever dem.',
            )}
          </p>
          <p className={s.hint}>{T('Ferdigheter er valgfritt. Vakttyper uten krav kan brukes som før.')}</p>
        </div>
      )}
      {!!skills.length && (
        <div data-testid="skill-list" className={s.list}>
          {skills.map((skill) => (
            <SkillRow
              key={skill.id}
              skill={skill}
              skills={skills}
              types={types}
              typesState={typesQ.isError ? 'error' : typesQ.isPending ? 'loading' : 'ready'}
              users={usersQ.data ?? []}
              usersReady={usersQ.isSuccess}
              usersError={usersQ.isError}
              retryUsers={() => void usersQ.refetch()}
              retryTypes={() => void typesQ.refetch()}
              mayEdit={mayEdit && !q.isError}
              canAssign={canAssign}
              reload={reload}
              onRemove={() => {
                setRemoving(skill);
                setRemoveProblem('');
                setRemoveConflict(false);
              }}
            />
          ))}
        </div>
      )}
      <Drawer
        open={!!removing}
        onClose={() => {
          if (!w.remove.isPending) setRemoving(null);
        }}
        blockClose={() => w.remove.isPending}
        title={T('Slette {name}?', { name: removing?.name ?? '' })}
        sub={T('Se gjennom hva som blir fjernet.')}
        footer={
          <>
            <Button onClick={() => setRemoving(null)} disabled={w.remove.isPending}>
              {T('Avbryt')}
            </Button>
            {removeConflict ? (
              <Button
                onClick={async () => {
                  if (await reload()) setRemoving(null);
                  else setRemoveProblem(T('Kunne ikke hente siste oversikt. Prøv igjen.'));
                }}
              >
                {T('Hent siste oversikt')}
              </Button>
            ) : (
              <Button tone="dgr" onClick={() => void remove()} disabled={w.remove.isPending}>
                {w.remove.isPending ? T('Sletter …') : T('Slett ferdigheten')}
              </Button>
            )}
          </>
        }
      >
        {removing && (
          <div className={s.deleteBody}>
            <p>
              {removing.people.length === 1
                ? T('Ferdigheten fjernes fra 1 ansatt.')
                : T('Ferdigheten fjernes fra {n} ansatte.', {
                    n: removing.people.length,
                  })}
            </p>
            <p>
              {removing.shift_types.length
                ? T('Kravet fjernes fra: {names}.', {
                    names: removing.shift_types
                      .map((id) => types.find((t) => t.id === id)?.name ?? T('Vakttype {n}', { n: id }))
                      .join(', '),
                  })
                : T('Ingen vakttyper krever denne ferdigheten.')}
            </p>
            <p>
              {T(
                'Vakter og ansatte beholdes. Denne ferdigheten vil ikke lenger begrense planleggingen eller gi advarsler i Vaktplan.',
              )}
            </p>
            <p>
              <strong>{T('Slettingen kan ikke angres.')}</strong>
            </p>
            {removeProblem && (
              <p className={s.error} role="alert">
                {removeProblem}
              </p>
            )}
          </div>
        )}
      </Drawer>
    </section>
  );
}

function SkillRow({
  skill,
  skills,
  types,
  typesState,
  users,
  usersReady,
  usersError,
  retryUsers,
  retryTypes,
  mayEdit,
  canAssign,
  reload,
  onRemove,
}: {
  skill: ApiSkill;
  skills: ApiSkill[];
  types: ShiftType[];
  typesState: 'loading' | 'error' | 'ready';
  users: ApiUser[];
  usersReady: boolean;
  usersError: boolean;
  retryUsers: () => void;
  retryTypes: () => void;
  mayEdit: boolean;
  canAssign: boolean;
  reload: () => Promise<ApiSkill[] | null>;
  onRemove: () => void;
}) {
  const w = useSkillWrites();
  const [name, setName] = useState(skill.name);
  const [selectedTypes, setSelectedTypes] = useState(skill.shift_types);
  const [revision, setRevision] = useState(skill.revision);
  const [problem, setProblem] = useState('');
  const [conflict, setConflict] = useState(false);
  const [status, setStatus] = useState('');
  const [attempt, setAttempt] = useState<SkillWrite | null>(null);
  const [choosing, setChoosing] = useState(false);
  const [person, setPerson] = useState('');
  const dirty = useRef(false);
  const busy = useRef(false);
  const [working, setWorking] = useState(false);
  const picker = useRef<HTMLSelectElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const errorId = `skill-error-${skill.id}`;
  useEffect(() => {
    if (!dirty.current && !working && !problem) {
      setName(skill.name);
      setSelectedTypes(skill.shift_types);
      setRevision(skill.revision);
    }
  }, [skill.name, skill.shift_types, skill.revision, working, problem]);
  useEffect(() => {
    if (choosing) picker.current?.focus();
  }, [choosing]);
  const save = async (body: SkillWrite) => {
    if (busy.current) return;
    busy.current = true;
    setWorking(true);
    setProblem('');
    setConflict(false);
    setAttempt(body);
    setStatus(T('Lagrer …'));
    try {
      const saved = await w.update.mutateAsync({ id: skill.id, body });
      dirty.current = false;
      setName(saved.name);
      setSelectedTypes(saved.shift_types);
      setRevision(saved.revision);
      setAttempt(null);
      setStatus(T('Lagret'));
    } catch (e) {
      setProblem(message(e));
      setConflict(e instanceof ApiError && e.code === 'skills_changed');
      setStatus('');
    } finally {
      busy.current = false;
      setWorking(false);
    }
  };
  const rename = () => {
    if (!dirty.current || busy.current) return;
    const clean = name.trim();
    if (!clean || clean.length > 80) {
      setProblem(T('Navnet må ha 1 til 80 tegn. Skriv et navn eller bruk lagret versjon.'));
      setAttempt(null);
      return;
    }
    if (clean === skill.name) {
      dirty.current = false;
      setName(clean);
      setProblem('');
      return;
    }
    void save({ name: clean, expected_revision: revision });
  };
  const restore = async () => {
    setWorking(true);
    const latest = await reload();
    if (!latest) {
      setProblem(T('Kunne ikke hente siste versjon. Utkastet ditt er beholdt. Prøv igjen.'));
      setWorking(false);
      return;
    }
    const current = latest.find((k) => k.id === skill.id);
    if (current) {
      dirty.current = false;
      setName(current.name);
      setSelectedTypes(current.shift_types);
      setRevision(current.revision);
      setProblem('');
      setConflict(false);
      setAttempt(null);
      setStatus(T('Siste versjon er hentet.'));
    }
    setWorking(false);
  };
  const names = skill.people.map((id) => users.find((u) => u.id === id)).filter((u): u is ApiUser => !!u);
  const unknown = skill.people.length - names.length;
  return (
    <article
      className={s.row}
      data-testid="skill"
      id={`ferdighet-${skill.id}`}
      tabIndex={-1}
      aria-label={skill.name}
    >
      <div className={s.nameBlock}>
        <label className="sr-only" htmlFor={`skill-name-${skill.id}`}>
          {T('Navn på ferdigheten {name}', { name: skill.name })}
        </label>
        <div className={s.nameLine}>
          <input
            ref={input}
            id={`skill-name-${skill.id}`}
            className={`inp ${s.name}`}
            value={name}
            disabled={!mayEdit || working}
            maxLength={80}
            aria-invalid={!!problem && (!attempt || !!attempt.name)}
            aria-describedby={problem ? errorId : `skill-save-${skill.id}`}
            onChange={(e) => {
              dirty.current = true;
              setName(e.target.value);
              setStatus(T('Ikke lagret'));
            }}
            onBlur={rename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                rename();
              }
            }}
          />
          {mayEdit && (
            <Button
              ghost
              tone="dgr"
              className={s.delete}
              aria-label={T('Slett ferdigheten {name}', { name: skill.name })}
              onClick={onRemove}
              disabled={working || dirty.current}
            >
              <Icon name="trash" />
              <span>{T('Slett')}</span>
            </Button>
          )}
        </div>
        <output className={s.save} id={`skill-save-${skill.id}`}>
          {status || (mayEdit ? T('Navn lagres når du forlater feltet.') : T('Kun lesing'))}
        </output>
        <p className={s.people} data-testid="skill-people">
          <Icon name="users" />
          <strong>
            {skill.people.length
              ? skill.people.length === 1
                ? T('1 ansatt har ferdigheten')
                : T('{n} ansatte har ferdigheten', { n: skill.people.length })
              : T('Ingen ansatte har ferdigheten ennå')}
          </strong>
        </p>
        {skill.people.length > 0 && (
          <p className={s.hint}>
            {usersReady ? (
              <>
                {names.map((u, i) => (
                  <span key={u.id}>
                    {i > 0 ? ', ' : ''}
                    <Link to={personUrl(u.id, skill.id)}>{fullName(u)}</Link>
                  </span>
                ))}
                {unknown > 0 && T(' · {n} navn er utilgjengelige', { n: unknown })}
              </>
            ) : (
              T('Antallet er kjent. Ansattnavn er ikke tilgjengelige ennå.')
            )}
          </p>
        )}
        {canAssign && (
          <button
            type="button"
            className={s.link}
            aria-expanded={choosing}
            onClick={() => setChoosing(!choosing)}
          >
            <Icon name="users" />
            {choosing ? T('Lukk ansattvalg') : T('Velg ansatte')}
          </button>
        )}
        {choosing && (
          <div className={s.picker}>
            <label htmlFor={`skill-person-${skill.id}`}>{T('Velg ansattkort')}</label>
            {usersError ? (
              <>
                <p role="alert">{T('Kunne ikke hente ansatte.')}</p>
                <Button onClick={retryUsers}>{T('Prøv igjen')}</Button>
              </>
            ) : !usersReady ? (
              <output>{T('Henter ansatte …')}</output>
            ) : users.length ? (
              <>
                <select
                  ref={picker}
                  className="sel"
                  id={`skill-person-${skill.id}`}
                  value={person}
                  onChange={(e) => setPerson(e.target.value)}
                >
                  <option value="">{T('Velg en ansatt')}</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {fullName(u)}
                      {skill.people.includes(u.id) ? T(' · har ferdigheten') : ''}
                    </option>
                  ))}
                </select>
                {person ? (
                  <Link className="btn" to={personUrl(Number(person), skill.id)}>
                    {T('Åpne ansattkort')}
                  </Link>
                ) : (
                  <Button disabled>{T('Åpne ansattkort')}</Button>
                )}
                <p className={s.hint}>{T('Tildel ferdigheten på kortet og kom tilbake hit etterpå.')}</p>
              </>
            ) : (
              <p>
                {T('Legg til ansatte før du tildeler ferdigheter.')}{' '}
                <Link to="/ansatte">{T('Åpne Ansatte')}</Link>
              </p>
            )}
          </div>
        )}
      </div>
      <div className={s.requirements}>
        <fieldset
          className={s.types}
          disabled={!mayEdit || working || !!problem || typesState !== 'ready'}
          aria-describedby={`skill-type-help-${skill.id}`}
        >
          <legend>{T('Kreves på vakttypene')}</legend>
          <p className={s.hint} id={`skill-type-help-${skill.id}`}>
            {T('Alle som jobber en valgt vakttype, må ha ferdigheten.')}
          </p>
          <div className={s.checks}>
            {typesState === 'loading' ? (
              <output>{T('Henter vakttyper …')}</output>
            ) : typesState === 'error' ? (
              <p>{T('Vakttypene kunne ikke hentes.')}</p>
            ) : types.length ? (
              types.map((t) => (
                <label key={t.id}>
                  <input
                    type="checkbox"
                    checked={selectedTypes.includes(t.id)}
                    onChange={(e) => {
                      const next = e.target.checked
                        ? [...selectedTypes, t.id]
                        : selectedTypes.filter((id) => id !== t.id);
                      setSelectedTypes(next);
                      void save({
                        shift_types: next,
                        expected_revision: revision,
                      });
                    }}
                  />
                  {t.name}
                </label>
              ))
            ) : (
              <p className={s.hint}>{T('Ingen vakttyper ennå.')}</p>
            )}
          </div>
        </fieldset>
        {typesState === 'error' && <Button onClick={retryTypes}>{T('Hent vakttyper på nytt')}</Button>}
        {typesState === 'ready' && !types.length && (
          <Link className={s.link} to="/innstillinger/typer">
            {T('Åpne vakttyper')}
          </Link>
        )}
        {typesState === 'ready' &&
          skill.shift_types.map((id) => {
            const required = skills.filter((k) => k.shift_types.includes(id));
            const holders =
              required[0]?.people.filter((uid) => required.every((k) => k.people.includes(uid))) ?? [];
            const typeName = types.find((t) => t.id === id)?.name ?? T('Vakttype {n}', { n: id });
            return (
              <p key={id} className={holders.length ? s.hint : s.warning}>
                {T(
                  holders.length === 1
                    ? '{type}: {n} ansatt har alle ferdighetene som kreves.'
                    : '{type}: {n} ansatte har alle ferdighetene som kreves.',
                  { type: typeName, n: holders.length },
                )}
              </p>
            );
          })}
        {!skill.shift_types.length && (
          <p className={s.hint}>{T('Ingen vaktkrav. Ferdigheten kan fortsatt tildeles ansatte.')}</p>
        )}
      </div>
      {problem && (
        <div className={s.rowError}>
          <p className={s.error} id={errorId} role="alert">
            {problem}
          </p>
          <div className={s.actions}>
            {attempt && !conflict && (
              <Button onClick={() => void save(attempt)} disabled={working}>
                {T('Prøv å lagre igjen')}
              </Button>
            )}
            <Button onClick={() => void restore()} disabled={working}>
              {conflict ? T('Hent siste versjon') : T('Bruk lagret versjon')}
            </Button>
          </div>
        </div>
      )}
    </article>
  );
}

export function FerdigheterTab() {
  const can = useCan();
  return <Ferdigheter mayEdit={can('innstillinger.endre')} />;
}
